import React from 'react';
import {REVIEWS, GOOGLE_REVIEWS_URL} from '../../data/reviews';
import '/src/assets/css/customer-reviews.css';

const AVATAR_COLORS = ['#d30000', '#b45309', '#1d7a99', '#1a7f37', '#7c3aed', '#be185d'];
const colorFor = (name) => AVATAR_COLORS[[...name].reduce((sum, ch) => sum + ch.charCodeAt(0), 0) % AVATAR_COLORS.length];

const Stars = ({count}) => (
    <span className="reviews__stars" role="img" aria-label={`${count} out of 5 stars`}>
        {'★'.repeat(count)}<span className="reviews__stars-off">{'★'.repeat(5 - count)}</span>
    </span>
);

const LONG_REVIEW = 190; // characters

// One review card. Long reviews are clamped to a few lines with a "Read more" toggle (full text stays in the page).
class ReviewCard extends React.Component {
    state = {open: false};

    render() {
        const {r} = this.props;
        const isLong = r.text.length > LONG_REVIEW;
        const {open} = this.state;
        return (
            <li className="reviews__card">
                <span className="reviews__quote-mark" aria-hidden="true">“</span>
                <Stars count={r.stars}/>
                <blockquote className={"reviews__text" + (isLong && !open ? " is-clamped" : "")}>{r.text}</blockquote>
                {isLong &&
                    <button type="button" className="reviews__toggle" aria-expanded={open}
                            onClick={() => this.setState({open: !open})}>
                        {open ? 'Show less' : 'Read more'}
                    </button>
                }
                <footer className="reviews__author">
                    <span className="reviews__avatar" style={{background: colorFor(r.name)}} aria-hidden="true">{r.name.charAt(0)}</span>
                    <span><strong>{r.name}</strong><small>Google review</small></span>
                </footer>
            </li>
        );
    }
}

/**
 * Reusable customer-reviews panel. Drop it on any page:
 *
 *   <CustomerReviews />                                   // all reviews
 *   <CustomerReviews tag="meat" />                        // only meat-market reviews
 *   <CustomerReviews tag="kitchen" limit={6} />           // 6 Fresh Kitchen reviews
 *   <CustomerReviews title="..." subtitle="..." />        // custom heading text
 *
 * Props: tag ('meat' | 'kitchen'), limit (number), title, subtitle, className.
 * The coloured background always spans the full screen width; the content stays centered.
 */
export default class CustomerReviews extends React.Component {
    list = React.createRef();
    band = React.createRef();

    componentDidMount() {
        this.fitToViewport();
        window.addEventListener('resize', this.fitToViewport);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.fitToViewport);
    }

    // Stretch the background band to the full width of the screen, regardless of the
    // (narrower, possibly off-center) page container it is placed in.
    fitToViewport = () => {
        const el = this.band.current;
        if (!el) return;
        el.style.marginLeft = '0px';
        el.style.width = 'auto';
        const left = el.getBoundingClientRect().left;
        el.style.marginLeft = `${-left}px`;
        el.style.width = `${document.documentElement.clientWidth}px`;
    };

    scrollBy = (direction) => {
        const el = this.list.current;
        if (!el) return;
        const card = el.querySelector('.reviews__card');
        const step = card ? card.getBoundingClientRect().width + 16 : el.clientWidth * 0.8;
        el.scrollBy({left: direction * step, behavior: 'smooth'});
    };

    render() {
        const {
            tag,
            limit,
            title = 'Loved by Houston families',
            subtitle = 'Real reviews from our neighbors on Google',
            className = '',
        } = this.props;
        let reviews = tag ? REVIEWS.filter(r => r.tags.includes(tag)) : REVIEWS;
        if (limit) reviews = reviews.slice(0, limit);
        if (!reviews.length) return null;

        return (
            <section className={`reviews ${className}`.trim()} aria-label="Customer reviews" ref={this.band}>
                <div className="reviews__inner">
                <div className="reviews__head">
                    <div>
                        <span className="reviews__eyebrow">★★★★★ Google reviews</span>
                        <h2 className="reviews__title">{title}</h2>
                        <p className="reviews__subtitle">{subtitle}</p>
                    </div>
                    <div className="reviews__controls">
                        <button type="button" className="reviews__arrow" aria-label="Previous reviews" onClick={() => this.scrollBy(-1)}>‹</button>
                        <button type="button" className="reviews__arrow" aria-label="Next reviews" onClick={() => this.scrollBy(1)}>›</button>
                    </div>
                </div>

                <ul className="reviews__list" ref={this.list}>
                    {reviews.map(r => <ReviewCard key={r.name} r={r}/>)}
                </ul>

                <a className="reviews__more" href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer">
                    See all reviews on Google →
                </a>
                </div>
            </section>
        );
    }
}
